import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import apiClient from '@/lib/api';
import {
  Mail, Lock, AlertCircle, ShieldCheck, User as UserIcon, Mail as MailIcon,
  Chrome, MessageSquare, Github, Gamepad2, KeyRound, CheckCircle2, Loader2
} from 'lucide-react';

const SCOPE_LABELS = {
  profile: { icon: UserIcon, label: 'View your profile', desc: 'Name, username and profile picture' },
  email: { icon: MailIcon, label: 'View your email address', desc: 'Your primary email' },
  openid: { icon: KeyRound, label: 'Verify your identity', desc: 'A stable account identifier' },
  offline_access: { icon: ShieldCheck, label: 'Stay signed in', desc: 'Refresh access while you are away' },
};

const PROVIDER_ICONS = {
  google: Chrome, discord: MessageSquare, github: Github,
  microsoft: Chrome, steam: Gamepad2, minecraft: Gamepad2,
};

const SSOAuthorizePage = () => {
  const [params] = useSearchParams();
  const clientId = params.get('client_id');
  const redirectUri = params.get('redirect_uri');
  const scope = params.get('scope') || 'profile email';
  const state = params.get('state');
  const codeChallenge = params.get('code_challenge');
  const codeChallengeMethod = params.get('code_challenge_method') || 'S256';
  const display = params.get('display') || 'redirect';
  const prompt = params.get('prompt'); // 'consent' forces the screen

  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [fatal, setFatal] = useState('');
  const [approving, setApproving] = useState(false);
  const [autoApproving, setAutoApproving] = useState(false);

  // inline login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [providers, setProviders] = useState([]);
  const [mockDialog, setMockDialog] = useState({ open: false, provider: null });
  const [mockUsername, setMockUsername] = useState('testuser');

  const isPopup = display === 'popup' && !!window.opener;
  const scopes = scope.split(' ').filter(Boolean);

  const deliver = useCallback((payload) => {
    if (isPopup) {
      window.opener.postMessage({ source: 'lostdc-auth', ...payload }, '*');
      window.close();
      return;
    }
    if (payload.error) {
      const u = new URL(redirectUri);
      u.searchParams.set('error', payload.error);
      if (state) u.searchParams.set('state', state);
      window.location.href = u.toString();
      return;
    }
    const u = new URL(redirectUri);
    u.searchParams.set('code', payload.code);
    if (payload.state) u.searchParams.set('state', payload.state);
    window.location.href = u.toString();
  }, [isPopup, redirectUri, state]);

  useEffect(() => {
    if (!clientId || !redirectUri) {
      setFatal('Missing client_id or redirect_uri.');
      setLoading(false);
      return;
    }
    const boot = async () => {
      try {
        const appRes = await apiClient.get(`/oauth/app-info?client_id=${encodeURIComponent(clientId)}`);
        setApp(appRes.data);
        if (!appRes.data.redirect_uris.includes(redirectUri)) {
          setFatal('This redirect URI is not registered for the application.');
          setLoading(false);
          return;
        }
      } catch (e) {
        setFatal('Unknown application. Check your client_id.');
        setLoading(false);
        return;
      }
      try {
        const me = await apiClient.get('/auth/me');
        setUser(me.data);
        // Skip repeat consent: auto-continue if the user already approved this app+scopes
        if (prompt !== 'consent') {
          try {
            const st = await apiClient.get(
              `/oauth/authorization-status?client_id=${encodeURIComponent(clientId)}&scope=${encodeURIComponent(scope)}`
            );
            if (st.data.authorized) setAutoApproving(true);
          } catch (_) {}
        }
      } catch (e) {
        setUser(null);
        apiClient.get('/providers').then(r => setProviders(r.data)).catch(() => {});
      }
      setLoading(false);
    };
    boot();
  }, [clientId, redirectUri]);

  const handleApprove = async () => {
    setApproving(true);
    setError('');
    try {
      const res = await apiClient.post('/oauth/authorize', {
        response_type: 'code',
        client_id: clientId,
        redirect_uri: redirectUri,
        scope,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallenge ? codeChallengeMethod : undefined,
      });
      deliver({ code: res.data.code, state: res.data.state });
    } catch (e) {
      setError(e.response?.data?.detail || 'Authorization failed');
      setApproving(false);
    }
  };

  const handleDeny = () => deliver({ error: 'access_denied' });

  // Auto-continue when the user has already granted this app
  useEffect(() => {
    if (autoApproving && user && !approving && !fatal) {
      handleApprove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApproving, user]);

  const handleUseAnother = async () => {
    try { await apiClient.post('/auth/logout'); } catch (_) {}
    setUser(null);
    apiClient.get('/providers').then(r => setProviders(r.data)).catch(() => {});
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    setError('');
    try {
      const res = await apiClient.post('/auth/login', { email, password });
      if (res.data.requires_2fa) {
        setError('This account uses 2FA. Please sign in at the account page first, then return.');
        setSigningIn(false);
        return;
      }
      const me = await apiClient.get('/auth/me');
      setUser(me.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setSigningIn(false);
    }
  };

  const handleMockLogin = async () => {
    setSigningIn(true);
    setError('');
    try {
      await apiClient.post(`/auth/mock/${mockDialog.provider.type}`, { username: mockUsername });
      const me = await apiClient.get('/auth/me');
      setUser(me.data);
      setMockDialog({ open: false, provider: null });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setSigningIn(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pista-glow pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(82_42%_60%/0.08),transparent_50%)] pointer-events-none" />
      <div className="w-full max-w-md relative z-10">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="text-center" data-testid="sso-loading">
          <Loader2 className="h-10 w-10 text-pista animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Preparing secure sign-in…</p>
        </div>
      </Shell>
    );
  }

  if (fatal) {
    return (
      <Shell>
        <Card className="border-border/60 bg-card/95" data-testid="sso-error-card">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" /> Cannot continue
            </CardTitle>
            <CardDescription>{fatal}</CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  if (autoApproving && user && !error) {
    return (
      <Shell>
        <div className="text-center" data-testid="sso-auto-continue">
          <Loader2 className="h-10 w-10 text-pista animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Continuing to {app?.name || 'the app'}…</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold tracking-tight">
          <span className="text-pista">Lost</span>DrkC <span className="text-muted-foreground font-normal text-lg">SSO</span>
        </h1>
      </div>

      <Card className="border-border/60 backdrop-blur-sm bg-card/95" data-testid="sso-consent-card">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-pista/15 flex items-center justify-center overflow-hidden">
              {app?.logo
                ? <img src={app.logo} alt={app.name} className="h-full w-full object-cover" />
                : <ShieldCheck className="h-6 w-6 text-pista" />}
            </div>
          </div>
          <CardTitle className="text-xl" data-testid="sso-app-name">{app?.name || 'Application'}</CardTitle>
          <CardDescription>
            wants to access your <span className="text-pista font-medium">LostDrkC</span> account
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4" data-testid="sso-error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {user ? (
            <div className="space-y-5" data-testid="sso-continue-view">
              {/* Continue as [name] */}
              <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-secondary/40">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-pista/20 text-pista">{user.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">This will allow {app?.name} to:</p>
                <ul className="space-y-2">
                  {scopes.map((s) => {
                    const info = SCOPE_LABELS[s] || { icon: CheckCircle2, label: s, desc: '' };
                    const Icon = info.icon;
                    return (
                      <li key={s} className="flex items-start gap-3 text-sm" data-testid={`sso-scope-${s}`}>
                        <Icon className="h-4 w-4 text-pista mt-0.5 flex-shrink-0" />
                        <div>
                          <div>{info.label}</div>
                          {info.desc && <div className="text-xs text-muted-foreground">{info.desc}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="space-y-2">
                <Button className="w-full" onClick={handleApprove} disabled={approving} data-testid="sso-continue-button">
                  {approving ? 'Authorizing…' : `Continue as ${user.name?.split(' ')[0] || 'me'}`}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={handleDeny} disabled={approving} data-testid="sso-deny-button">
                    Cancel
                  </Button>
                  <Button variant="ghost" className="flex-1" onClick={handleUseAnother} disabled={approving} data-testid="sso-use-another">
                    Use another account
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="sso-login-view">
              <p className="text-sm text-muted-foreground text-center">Sign in to continue</p>

              {providers.filter(p => p.mocked).length > 0 && (
                <div className="space-y-2">
                  {providers.filter(p => p.mocked).map((provider) => {
                    const Icon = PROVIDER_ICONS[provider.type] || Chrome;
                    return (
                      <Button
                        key={provider.provider_id}
                        variant="outline"
                        className="w-full"
                        onClick={() => { setMockDialog({ open: true, provider }); setMockUsername('testuser'); }}
                        data-testid={`sso-${provider.type}-button`}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        Continue with {provider.name}
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-pista/20 text-pista">MOCK</span>
                      </Button>
                    );
                  })}
                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">Or</span></div>
                  </div>
                </div>
              )}

              <form onSubmit={handleEmailLogin} className="space-y-3">
                <div>
                  <Label htmlFor="sso-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                    <Input id="sso-email" data-testid="sso-email-input" type="email" className="pl-10"
                      placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="sso-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                    <Input id="sso-password" data-testid="sso-password-input" type="password" className="pl-10"
                      placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={signingIn} data-testid="sso-login-button">
                  {signingIn ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground mt-5">
            Authorizing will redirect you to{' '}
            <span className="font-mono">{(() => { try { return new URL(redirectUri).host; } catch { return redirectUri; } })()}</span>
          </p>
        </CardContent>
      </Card>

      <Dialog open={mockDialog.open} onOpenChange={(open) => setMockDialog({ ...mockDialog, open })}>
        <DialogContent data-testid="sso-mock-dialog">
          <DialogHeader>
            <DialogTitle>Mock {mockDialog.provider?.name} Login</DialogTitle>
            <DialogDescription>Enter a username to simulate this OAuth provider.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="sso-mock-username">Username</Label>
            <Input id="sso-mock-username" data-testid="sso-mock-username-input" value={mockUsername}
              onChange={(e) => setMockUsername(e.target.value)} placeholder="testuser" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMockDialog({ open: false, provider: null })}>Cancel</Button>
            <Button onClick={handleMockLogin} disabled={signingIn || !mockUsername} data-testid="sso-mock-submit">
              {signingIn ? 'Signing in…' : `Continue as ${mockUsername || '...'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
};

export default SSOAuthorizePage;
